import { createEnvelopeSource } from './create-envelope-source';
import type {
    KpiCardDataRequest,
    KpiCardState,
    KpiSource,
    KpiSourceDeps,
} from '../types';
import type { ColumnDatum } from '../forms/columns-form.helpers';
import { ROAD_TO_REWARDS_CAMPAIGN_ID } from './road-to-rewards-campaign';

/** The six rungs that grant a reward, visually marked on the chart. The back
 * serves fifteen flat ordinals; this is the CMS's own marking decision. */
const REWARD_MILESTONES: ReadonlySet<number> = new Set([3, 5, 7, 10, 13, 15]);

const base = createEnvelopeSource({
    label: 'Campaign milestones',
    endpoint: 'analytics/campaign-milestones-member',
    metrics: [
        { id: 'membersOnLadder', label: 'Members on ladder', type: 'scalar' },
    ],
    forms: ['columns'],
    regionGrain: 'province',
    staticParams: { campaignId: ROAD_TO_REWARDS_CAMPAIGN_ID },
    columnsAxisCells: {
        valueCell: 'members',
        subLabelCell: 'avgDaysFromPrevious',
    },
});

/** The fifteen-bar visit-milestone histogram. The ladder is exclusive: rung N
 * is members whose total counted visits is exactly N, so milestone 1 (members
 * with only their enrollment bonus) is real and plotted like every other rung.
 * The endpoint ignores the selected period itself, so no period handling is
 * needed here. */
export const campaignMilestonesSource: KpiSource<'membersOnLadder'> = {
    descriptor: base.descriptor,

    async getCardData(
        req: KpiCardDataRequest,
        deps: KpiSourceDeps
    ): Promise<KpiCardState> {
        const state = await base.getCardData(
            { ...req, breakdownBy: 'visitMilestone' },
            deps
        );
        if (state.status !== 'ready' || state.data.form !== 'columns') {
            return state;
        }

        const columns: ColumnDatum[] = state.data.columns.map(
            (column, index): ColumnDatum => {
                const ordinal = index + 1;
                return {
                    ...column,
                    label: `${ordinal} visits`,
                    ...(REWARD_MILESTONES.has(ordinal) && { emphasis: true }),
                };
            }
        );

        return { ...state, data: { ...state.data, columns } };
    },
};
